import { useState } from "react";
import { Carousel } from "react-bootstrap";
import { DocumentSubType } from "@/briefpoint-client";
import Button from "@/components/Button";
import { Modal } from "@/components/Modals";
import download from "@/images/tutorial-download.png";
import getStarted from "@/images/tutorial-get-started.png";
import objectRespond from "@/images/tutorial-object-respond.png";
import uploadImage from "@/images/tutorial-upload-doc.png";
import { CaliforniaId } from "@/lib/constants";
import { getSubTypeText } from "@/lib/utils";
import styles from "./IntroTutorialModal.module.scss";

interface Props {
  isShow: boolean;
  onClose: () => void;
  fileUpload: (e: React.MouseEvent) => void;
  jurisdictionId: string;
}

export default function IntroTutorialModal({
  isShow,
  jurisdictionId,
  onClose,
  fileUpload,
}: Props) {
  const [index, setIndex] = useState(0);

  const handleSelect = (selectedIndex: number) => {
    setIndex(selectedIndex);
  };

  const handleUploadClick = (e: React.MouseEvent) => {
    fileUpload(e);
    onClose();
  };
  function modalContent() {
    return (
      <>
        <Carousel
          className="tutorial"
          activeIndex={index}
          onSelect={handleSelect}
          interval={null}
          controls={false}
          indicators={false}
          wrap={false}
        >
          <Carousel.Item>
            <h3>Welcome to Briefpoint!</h3>
            <p className="lead">
              Briefpoint takes your opposing counsel’s discovery request PDFs
              and
              <br /> turns them into servable responses within minutes.
            </p>
            <p className="lead">
              You’ll start by uploading your own document or use our sample for
              a test run.
            </p>
            <img
              className={styles.slideIllustration}
              src={uploadImage}
              alt="Briefpoint document list with upload and respond actions highlighted"
            />
          </Carousel.Item>
          <Carousel.Item>
            <h3>Next, Confirm Details</h3>
            <p className="lead">
              This process allows you to verify some of the information that's
              been extracted.
            </p>
            <img
              className={`${styles.slideIllustration} ${styles.slideIllustrationRounded}`}
              src={getStarted}
              alt="Discovery request setup step confirming extracted case and party details"
            />
          </Carousel.Item>
          <Carousel.Item>
            <div className={styles.stepThree}>
              <h3>Object and Respond</h3>
              <p className={styles.stepThreeIntro}>
                Next, you’ll respond to your discovery request by objecting and
                adding responses. Get a jump start by using Auto Object which
                uses over 7,000 pieces of default content to suggest objections
                for you.
              </p>
              <div className={styles.stepThreeFrame}>
                <img
                  className={styles.stepThreeImage}
                  src={objectRespond}
                  alt="Add to Response sidebar with Auto Object and objection checklist next to a special interrogatory response"
                />
              </div>
              <div className={styles.stepThreeFooter}>
                <p className={styles.stepThreeFooterLine}>
                  Don’t like the phrasing? You can customize each objection!
                </p>
                <p className={styles.stepThreeFooterLine}>
                  Want to make it all your own? Head over to the Library to
                  customize your content.
                </p>
              </div>
            </div>
          </Carousel.Item>
          <Carousel.Item>
            <h3>Download, Finalize and Serve!</h3>
            <p className="lead">
              Once you’re satisfied with your response document, click “Open in
              Word”, and
              <br /> a .docx file of your response will be downloaded to your
              computer.
            </p>
            <img
              className={styles.slideIllustration}
              src={download}
              alt="Download response as a Word document"
            />
          </Carousel.Item>
          <Carousel.Item>
            <div className={styles.stepFive}>
              <h3>Let’s get the ball rolling!</h3>
              <p className={styles.stepFiveLead}>
                Get started by uploading your first document.
              </p>
              <div className={styles.stepFiveDocBox}>
                <p className={styles.stepFiveDocBoxTitle}>
                  Briefpoint currently accepts the following court documents:
                </p>
                <div className={styles.stepFiveDocBoxDivider} />
                <div className={styles.stepFiveListRow}>
                  <ul className={styles.stepFiveListColumn}>
                    <li>
                      {
                        getSubTypeText(
                          DocumentSubType.RequestsForAdmission,
                          jurisdictionId,
                        ).plural
                      }
                    </li>
                    <li>
                      {
                        getSubTypeText(
                          DocumentSubType.RequestsForProduction,
                          jurisdictionId,
                        ).plural
                      }
                    </li>
                    {jurisdictionId === CaliforniaId && (
                      <li>Third Party Depo Subpoenas (SUBP-010)</li>
                    )}
                  </ul>
                  <ul className={styles.stepFiveListColumn}>
                    <li>Interrogatories</li>
                    <li>Notices of Depositions with RFPs Attached</li>
                    {jurisdictionId === CaliforniaId && (
                      <li>
                        {
                          getSubTypeText(
                            DocumentSubType.FormInterrogatories,
                            jurisdictionId,
                          ).plural
                        }
                      </li>
                    )}
                  </ul>
                </div>
              </div>
              <Button
                size="lg"
                onClick={(e) => handleUploadClick(e)}
                className={styles.stepFiveCta}
              >
                Start Responding
              </Button>
              <div className={styles.stepFiveDemo}>
                <p className={styles.stepFiveDemoText}>
                  Want to schedule a live Briefpoint demo or training session?
                </p>
                <a
                  className={styles.stepFiveDemoLink}
                  href="https://calendly.com/d/cqn6-9yx-wwq/briefpoint-demo"
                  target="_blank"
                  rel="noreferrer"
                  title="Schedule Demo/Training"
                >
                  Find a time for you and your team.
                </a>
              </div>
            </div>
          </Carousel.Item>
        </Carousel>
        <div className={styles.content}>
          <span>{index + 1} of 5</span>
          <div className={styles.pagerActions}>
            {index > 0 && (
              <Button variant="outline" onClick={() => handleSelect(index - 1)}>
                Back
              </Button>
            )}
            {index < 4 && (
              <Button onClick={() => handleSelect(index + 1)}>Next</Button>
            )}
          </div>
        </div>
        <div className={styles.footerNote}>
          Briefpoint access limited to licensed attorneys and their agents.
          <br />
          All content available on Briefpoint is strictly illustrative and does
          not constitute legal advice.
        </div>
      </>
    );
  }

  return (
    <Modal
      show={isShow}
      onClose={onClose}
      onConfirm={onClose}
      size="lg"
      showCancelButton={false}
      showConfirmButton={false}
      confirmText={"Get Started!"}
      preventBackgroundClick
    >
      {modalContent()}
    </Modal>
  );
}
